// 본문 그리기. **블록 트리를 그대로 그린다** — 여기서 마크다운을 파싱하지 않는다.
//
// 파서를 화면에 두면 러스트 쪽 파서와 둘이 되고, 둘이 갈리는 날 검색 결과와
// 화면이 서로 다른 글을 보인다. 서식은 쓰기 한 번, 읽기는 그리기만이다.

import type { Block, Element, GroupId, Span, UserId } from '@maru/protocol'
import { lazy, Suspense, useState, useSyncExternalStore } from 'react'
import { AttachmentView, type FileMap } from './attachment_view.tsx'
import { why } from './confirm.tsx'
import { Button, ButtonLink, type ButtonVariant } from './kit/button.tsx'
import { Select } from './kit/field.tsx'

/**
 * `:이름:` → 그림 주소. 모르는 이름이면 `undefined` 를 준다.
 *
 * 조직마다 다른 그림이라 이름만으로는 못 그린다. 모르면 글자 그대로 `:이름:` 을
 * 남긴다 — 빈자리를 남기면 사람이 뭘 썼는지조차 못 읽는다.
 */
export type EmojiLook = (name: string) => string | undefined

/** 그룹 번호 → 부르는 이름과 "내가 그 안에 있는가". */
export type GroupLook = (group: GroupId) => { handle: string; mine: boolean } | undefined

/** 사람 이름과 '나' 는 옵션이다 — 모르면 번호로 그린다. */
export interface Names {
  name_of?: ((user: UserId) => string | undefined) | undefined
  me?: UserId | undefined
  /**
   * 사람 멘션을 눌렀다 — 그 사람의 쪽지창을 연다. 없으면 멘션은 글자로만 선다. 이름을 누르면
   * 열리는데 글 속 `@이름` 은 안 열려, 누구를 부른 것인지 보려면 구성원 판을 뒤져야 했다(3차 F210).
   */
  on_who?: ((user: UserId) => void) | undefined
  /**
   * 그룹 번호 → 부르는 이름과 "내가 그 안에 있는가".
   *
   * 안에 있으면 나를 부른 것처럼 그린다. 그룹 멘션은 알림이 실제로 오는 자리라,
   * 남을 부른 것처럼 옅게 그리면 사람이 자기 알림을 놓친다.
   */
  group_of?: GroupLook | undefined
  /** 커스텀 이모지 이름 → 그림 주소. 없으면 전부 글자로 남는다. */
  emoji_of?: EmojiLook | undefined
  /** 첨부 id → 받을 곳. 없으면 "불러오는 중". */
  files?: FileMap | undefined
  /**
   * 앱의 단추를 눌렀다(`block` 번째 블록의 `action`, 고르기면 고른 값). 없으면 단추는
   * 잠긴 채 그려진다. 거절되면 그 자리에 까닭을 띄운다.
   */
  on_action?: ((block: number, action: string, value?: string) => Promise<void>) | undefined
}

/** 서버가 준 까닭(`code`·`message`)을 사람 말로. 모르는 것은 받은 문장 그대로. */
export interface BodyViewProps extends Names {
  body: Block[]
}

export function BodyView({
  body,
  name_of,
  me,
  files,
  group_of,
  emoji_of,
  on_action,
  on_who,
}: BodyViewProps) {
  return (
    <div className="BodyView">
      {body.map((block, i) => (
        // 블록에는 안정된 id 가 없다. 본문은 통째로 갈아 끼워지므로(수정은 새 본문이
        // 온다) 자리 index 로 충분하다 — 부분 재정렬이 일어나지 않는 목록이다.
        <BlockView
          key={i}
          at={i}
          block={block}
          name_of={name_of}
          me={me}
          files={files}
          group_of={group_of}
          emoji_of={emoji_of}
          on_action={on_action}
          on_who={on_who}
        />
      ))}
    </div>
  )
}

function BlockView({
  at,
  block,
  name_of,
  me,
  files,
  group_of,
  emoji_of,
  on_action,
  on_who,
}: { at: number; block: Block } & Names) {
  const runs = (spans: Span[]) =>
    spans.map((span, i) => (
      <SpanView
        key={i}
        span={span}
        name_of={name_of}
        me={me}
        group_of={group_of}
        emoji_of={emoji_of}
        on_who={on_who}
      />
    ))

  switch (block.t) {
    case 'para':
      return <p>{runs(block.spans)}</p>
    case 'quote':
      return <blockquote>{runs(block.spans)}</blockquote>
    case 'code':
      return <CodeView code={block.code} lang={block.lang} />
    case 'list': {
      const entries = block.entries.map((spans, i) => <li key={i}>{runs(spans)}</li>)
      return block.ordered ? <ol>{entries}</ol> : <ul>{entries}</ul>
    }
    case 'divider':
      return <hr />
    case 'attachment':
      return <AttachmentView id={block.id} handout={files?.get(block.id)} />
    // 레이아웃 블록은 앱만 쓴다. 제목은 글자 그대로 — 서식도 링크도 없다.
    case 'header':
      return <h3 className="header">{block.text}</h3>
    case 'section':
      return (
        <div className="section">
          {block.accessory != null && (
            <Controls at={at} elements={[block.accessory]} on_action={on_action} />
          )}
          {block.spans.length > 0 && <p>{runs(block.spans)}</p>}
          {block.fields !== undefined && block.fields.length > 0 && (
            <div className="fields">
              {block.fields.map((field, i) => (
                <p key={i}>{runs(field)}</p>
              ))}
            </div>
          )}
        </div>
      )
    case 'image':
      // 주소는 서버가 절대 https 만 통과시킨다(`message::image_url`). 남의 서버로 가는
      // 요청이라 리퍼러를 떼고, 화면 밖이면 받지 않는다.
      return (
        <figure className="image">
          <img src={block.url} alt={block.alt} loading="lazy" referrerPolicy="no-referrer" />
          {block.title != null && block.title !== '' && <figcaption>{block.title}</figcaption>}
        </figure>
      )
    case 'context':
      return <p className="context">{runs(block.spans)}</p>
    case 'actions':
      return <Controls at={at} elements={block.elements} on_action={on_action} />
  }
}

/** 앱이 고른 단추 모양 → 우리 단추 변형. 모르는 것은 보통 단추다. */
function variant_of(style: string | null | undefined): ButtonVariant {
  if (style === 'primary') return 'primary'
  if (style === 'danger') return 'danger'
  return 'secondary'
}

/**
 * 단추·고르기 한 줄. 누르는 동안은 잠그고, 거절되면 그 줄 밑에 까닭을 띄운다 —
 * 조용히 실패하면 사람은 앱이 일을 했다고 믿는다.
 */
function Controls({
  at,
  elements,
  on_action,
}: {
  at: number
  elements: Element[]
  on_action: Names['on_action']
}) {
  const [busy, set_busy] = useState(false)
  const [said, set_said] = useState<string | null>(null)
  const [chosen, set_chosen] = useState<Record<string, string>>({})
  const fire = (action: string, value?: string) => {
    if (on_action === undefined) return
    set_busy(true)
    set_said(null)
    on_action(at, action, value).then(
      () => set_busy(false),
      (err: unknown) => {
        set_busy(false)
        set_said(why(err))
      },
    )
  }
  const locked = busy || on_action === undefined
  return (
    <div className="actions">
      {elements.map((e) =>
        e.t === 'button' ? (
          e.url != null ? (
            // 주소 단추는 링크로 연다(서버가 http(s) 만 통과시킨다). 그래도 앱에게 알린다.
            <ButtonLink
              key={e.action}
              className="action"
              variant={variant_of(e.style)}
              size="sm"
              href={e.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => fire(e.action)}
            >
              {e.text}
            </ButtonLink>
          ) : (
            <Button
              key={e.action}
              size="sm"
              variant={variant_of(e.style)}
              className="action"
              disabled={locked}
              onClick={() => fire(e.action)}
            >
              {e.text}
            </Button>
          )
        ) : (
          <Select
            key={e.action}
            size="sm"
            className="action"
            aria-label={e.placeholder}
            disabled={locked}
            value={chosen[e.action] ?? ''}
            onChange={(ev) => {
              const value = ev.currentTarget.value
              set_chosen((c) => ({ ...c, [e.action]: value }))
              fire(e.action, value)
            }}
          >
            <option value="" disabled>
              {e.placeholder}
            </option>
            {e.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.text}
              </option>
            ))}
          </Select>
        ),
      )}
      {said !== null && (
        <p className="action-said" role="status">
          {said}
        </p>
      )}
    </div>
  )
}

/** 표시 속성을 중첩된 태그로 감싼다. `<b><i>` 순서는 의미가 없으므로 고정해 둔다. */
function dressed(span: Extract<Span, { t: 'text' }>): React.ReactNode {
  let node: React.ReactNode = span.text
  if (span.code) node = <code>{node}</code>
  if (span.strike) node = <s>{node}</s>
  if (span.italic) node = <i>{node}</i>
  if (span.bold) node = <b>{node}</b>
  return node
}

// 표준 이모지 이름(`:tada:`)의 그림(3차 F131). 이름 표는 반응 고르기 청크에 있어 첫 화면에 싣지 않는다 —
// 모르는 이름이 처음 그려지거나 작성칸에 `:이름` 을 칠 때 그 청크를 받고, 받는 동안은 글자 그대로 둔다.
let named: ReadonlyMap<string, string> | undefined
let asked = false
const heard = new Set<() => void>()

/** 이름 표를 받는다(한 번만). 받으면 듣는 쪽을 전부 깨운다. */
export function want_emoji() {
  if (asked) return
  asked = true
  void import('./emoji_pick.tsx').then((m) => {
    named = new Map(
      m.EMOJI_GROUPS.flatMap((g) => g.glyphs.flatMap(([e, n]) => n.split(' ').map((k) => [k, e]))),
    )
    for (const r of heard) r()
  })
}

/** `useSyncExternalStore` 의 듣기·읽기. 듣기만으로는 표를 받지 않는다. */
export function hear_emoji(ring: () => void) {
  heard.add(ring)
  return () => heard.delete(ring)
}
export const emoji_table = () => named

function hear(ring: () => void) {
  want_emoji()
  return hear_emoji(ring)
}

function NamedEmoji({ name }: { name: string }) {
  const glyph = useSyncExternalStore(
    hear,
    () => named?.get(name),
    () => undefined,
  )
  return <span className="emoji">{glyph ?? `:${name}:`}</span>
}

/** 조각 하나. `inert` 면 링크를 글자로만(누르는 줄 안의 미리보기, `search_panel.tsx`). */
export function SpanView({
  span,
  name_of,
  me,
  group_of,
  emoji_of,
  on_who,
  inert = false,
}: { span: Span; inert?: boolean } & Names) {
  switch (span.t) {
    case 'text':
      return <>{dressed(span)}</>
    case 'mention_user': {
      const name = name_of?.(span.user)
      const look = `mention${me === span.user ? ' me' : ''}`
      if (on_who === undefined || inert) return <span className={look}>@{name ?? span.user}</span>
      return (
        <button
          type="button"
          className={look}
          onClick={(e) => {
            // 줄을 누른 것으로 번지면 좁은 폭에서 손이 함께 열린다.
            e.stopPropagation()
            on_who(span.user)
          }}
        >
          @{name ?? span.user}
        </button>
      )
    }
    case 'mention_group': {
      const found = group_of?.(span.group)
      return (
        <span className={`mention${found?.mine === true ? ' me' : ''}`}>
          @{found?.handle ?? span.group}
        </span>
      )
    }
    case 'mention_all':
      // `@here`·`@channel` 은 언제나 '나를 부른 것' 으로 그린다. 방 전체를 부른 것이니
      // 맞다 — 여기서 조용히 흘리면 공지가 눈에 안 띈다.
      return <span className="mention me">@{span.here ? 'here' : 'channel'}</span>
    case 'link':
      if (inert) return <span className="Link">{span.view ?? span.url}</span>
      return (
        // 새 탭으로 여는 링크에는 `noreferrer` 가 필요하다. 없으면 연 페이지가
        // `window.opener` 로 이쪽 탭을 바꿔치기할 수 있다.
        <a className="Link" href={span.url} target="_blank" rel="noreferrer noopener">
          {span.view ?? span.url}
        </a>
      )
    case 'emoji': {
      const url = emoji_of?.(span.name)
      // 주소는 서버에서 https 만 통과한다(domain/custom_emoji.rs). 여기서 다시
      // 재지 않는 이유는 규칙이 두 벌이 되면 언젠가 갈리기 때문이다.
      return url === undefined ? (
        <NamedEmoji name={span.name} />
      ) : (
        <img className="emoji custom" src={url} alt={`:${span.name}:`} title={`:${span.name}:`} />
      )
    }
  }
}

// 코드 블록(색칠·복사)은 첫 코드 블록이 설 때 받는다 — 첫 화면 바이트에 들지 않는다(code_block.tsx).
// 오는 동안에도 글자는 그대로 선다: 받기 전 자리표시가 같은 `<pre>` 다.
const CodeBlockLazy = lazy(() => import('./code_block.tsx').then((m) => ({ default: m.CodeBlock })))

function CodeView({ code, lang }: { code: string; lang?: string | null | undefined }) {
  const plain = (
    <pre className={lang ? `code ${lang}` : 'code'}>
      <code>{code}</code>
    </pre>
  )
  return (
    <Suspense fallback={plain}>
      <CodeBlockLazy code={code} lang={lang} />
    </Suspense>
  )
}
